/**
 * Helpers for fuzzy-matched clause highlighting on a continuous PDF preview.
 *
 * Ported from `dashboard/src/components/PdfViewer.tsx` so the Word add-in can
 * reuse the same threshold / overlay / scroll-to-rect behavior when the user
 * clicks "Go to" on a Sales Agreement clause.
 *
 * The rect coordinates returned by `/pdf-previewer/highlight-clauses` are in
 * the PDF's native coordinate space (scale 1). Callers must scale them to the
 * canvas's actual rendered scale before drawing, hence `computeFitScale`.
 */

/** Min similarity (0–1) accepted from the backend matcher. Mirrors the dashboard
 * threshold (`CLAUSE_MATCH_SIMILARITY_THRESHOLD`) and the backend's
 * `PDFPreviewerService.similarity_threshold`. */
export const CLAUSE_MATCH_SIMILARITY_THRESHOLD = 0.25;

/**
 * Build the same search string the dashboard sends when the user clicks a
 * clause-card header (`getTextForClause` + `PdfViewer.highlightClauses`).
 *
 * The backend matcher (`PDFPreviewerService._extract_clause_number_prefix`)
 * only anchors on a dotted number at the *start* of the text. If the stored
 * Sales Agreement body omitted that number, prepend `clause_number` so Word
 * "Go to" uses the same clause-number-anchored path as the dashboard.
 *
 * Do not collapse internal whitespace — the dashboard sends the field as-is.
 */
export function buildClauseHighlightSearchText(
  body: string | undefined | null,
  clauseNumber?: string | number | null,
): string {
  const text = (body ?? '').trim();
  if (!text) return '';
  const number = String(clauseNumber ?? '').trim();
  if (!number || number === '?' || number === 'לא נמצא') return text;
  const escaped = number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (new RegExp(`^${escaped}(?:\\.|\\s|$)`).test(text)) return text;
  return `${number} ${text}`;
}

export interface PdfRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface HighlightMatch {
  /** 0-based page index from the backend. */
  page: number;
  rect: PdfRect;
  text: string;
  similarity: number;
}

/** Sort matches by similarity desc, drop those below threshold, return the best. */
export function pickBestMatch(matches: HighlightMatch[]): HighlightMatch | null {
  if (!matches.length) return null;
  const sorted = [...matches].sort(
    (a, b) => (b.similarity || 0) - (a.similarity || 0),
  );
  const best = sorted[0];
  if (!best || (best.similarity || 0) < CLAUSE_MATCH_SIMILARITY_THRESHOLD) {
    return null;
  }
  return best;
}

/**
 * Compute the fit-to-container scale used when rendering a PDF page into the
 * panel's canvas. Matches the formula in `PastNegotiationSourcePdfPanel`'s
 * `computeScale`: max width minus 16px gutter, capped at 2.5x.
 */
export function computeFitScale(originalWidth: number, containerWidth: number): number {
  if (!originalWidth || !containerWidth) return 1;
  const maxW = Math.max(200, containerWidth - 16);
  return Math.min(maxW / originalWidth, 2.5);
}

/** Scale a PDF-native rect into rendered-canvas pixel coordinates. */
export function scaleRect(rect: PdfRect, scale: number): PdfRect {
  return {
    x0: rect.x0 * scale,
    y0: rect.y0 * scale,
    x1: rect.x1 * scale,
    y1: rect.y1 * scale,
  };
}

/**
 * Get or create the absolutely-positioned highlight layer aligned to the
 * page's canvas. The page wrapper centers its canvas (it can be narrower than
 * the wrapper itself), so a fill-the-wrapper layer would mis-align horizontally.
 * We instead size the layer to the canvas's exact offset + dimensions.
 *
 * The page wrapper must be `position: relative` for the absolute layer to
 * anchor to it.
 */
export function ensureHighlightLayer(pageEl: HTMLElement): HTMLDivElement | null {
  const canvas = pageEl.querySelector<HTMLCanvasElement>('canvas');
  if (!canvas) return null;
  let layer = pageEl.querySelector<HTMLDivElement>(':scope > .pdf-clause-highlight-layer');
  if (!layer) {
    layer = document.createElement('div');
    layer.className = 'pdf-clause-highlight-layer';
    layer.style.position = 'absolute';
    layer.style.pointerEvents = 'none';
    pageEl.appendChild(layer);
  }
  layer.style.left = `${canvas.offsetLeft}px`;
  layer.style.top = `${canvas.offsetTop}px`;
  layer.style.width = `${canvas.clientWidth}px`;
  layer.style.height = `${canvas.clientHeight}px`;
  return layer;
}

/** Remove all highlight layers within a scroll root. */
export function clearAllHighlights(root: HTMLElement): void {
  root.querySelectorAll('.pdf-clause-highlight-layer').forEach((el) => {
    el.parentElement?.removeChild(el);
  });
}

/**
 * Append a yellow rectangle overlay onto the given page's highlight layer.
 * `scaledRect` should already be in rendered-canvas pixels (use `scaleRect`).
 * Returns null if the canvas hasn't been mounted yet.
 */
export function addHighlightToPage(
  pageEl: HTMLElement,
  scaledRect: PdfRect,
): HTMLDivElement | null {
  const layer = ensureHighlightLayer(pageEl);
  if (!layer) return null;
  const highlight = document.createElement('div');
  highlight.className = 'pdf-clause-highlight';
  highlight.style.position = 'absolute';
  highlight.style.left = `${scaledRect.x0}px`;
  highlight.style.top = `${scaledRect.y0}px`;
  highlight.style.width = `${Math.max(0, scaledRect.x1 - scaledRect.x0)}px`;
  highlight.style.height = `${Math.max(0, scaledRect.y1 - scaledRect.y0)}px`;
  highlight.style.backgroundColor = 'rgba(255, 255, 0, 0.3)';
  highlight.style.border = '1px solid rgba(255, 255, 0, 0.7)';
  highlight.style.borderRadius = '2px';
  highlight.style.pointerEvents = 'none';
  layer.appendChild(highlight);
  return highlight;
}

export interface ScrollToHighlightOptions {
  /** Px of breathing room above the highlight after scrolling. */
  topPadding?: number;
  /** Smooth vs auto. Defaults to 'smooth'. */
  behavior?: ScrollBehavior;
  /** Max retry attempts while waiting for the page canvas to render. */
  maxAttempts?: number;
}

/**
 * Scroll the given root so the highlight at `pageEl + scaledRect` is in view.
 *
 * Uses the same exponential-backoff retry as the dashboard `PdfViewer`:
 * waits for the page's canvas to render before computing the final offset,
 * then falls back to a forced scroll on the last attempt. The panel renders
 * pages lazily, so the canvas may not exist yet when this is first called.
 */
export function scrollToHighlight(
  scrollRoot: HTMLElement,
  pageEl: HTMLElement,
  scaledRect: PdfRect,
  options: ScrollToHighlightOptions = {},
): void {
  const { topPadding = 80, behavior = 'smooth', maxAttempts = 10 } = options;

  const performScroll = () => {
    const pageTop = pageEl.offsetTop || 0;
    // `scaledRect.y0` is relative to the canvas's top-left, but the canvas
    // itself may be offset within the page wrapper (e.g. due to flex centering
    // or padding), so include `canvas.offsetTop` to land on the right Y.
    const canvas = pageEl.querySelector<HTMLCanvasElement>('canvas');
    const canvasTop = canvas?.offsetTop ?? 0;
    const targetScrollTop = Math.max(
      0,
      pageTop + canvasTop + (scaledRect.y0 || 0) - topPadding,
    );
    scrollRoot.scrollTo({ top: targetScrollTop, behavior });
  };

  const attempt = (n: number) => {
    const canvas = pageEl.querySelector('canvas');
    const ready = !!canvas && pageEl.offsetHeight > 0;
    if (ready || n >= maxAttempts) {
      performScroll();
      return;
    }
    const delay = 100 * Math.pow(2, n);
    window.setTimeout(() => attempt(n + 1), delay);
  };

  attempt(0);
}
